import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  Box,
  Paper,
  TextField,
  Button,
  Typography,
  Chip,
  FormControlLabel,
  Switch,
  Grid,
  Divider,
  Alert,
  Card,
  CardContent,
  IconButton,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import {
  Save as SaveIcon,
  ContentCopy as CopyIcon,
  Check as CheckIcon,
} from '@mui/icons-material';
import { toast } from 'react-toastify';
import { snippetApi } from '../../services/api';
import PageBackButton from '../../../components/PageBackButton';
import type { SnippetCreate } from '../../types';

const SnippetDetail: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const isDark = theme.palette.mode === 'dark';
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const isNew = id === 'new';

  // Form state
  const [formData, setFormData] = useState<SnippetCreate>({
    name: '',
    description: '',
    content: '',
    category: '',
    tags: [],
    is_active: true,
  });
  const [tagInput, setTagInput] = useState('');
  const [copied, setCopied] = useState(false);
  const [variables, setVariables] = useState<string[]>([]);

  // Fetch snippet data if editing
  const { data: snippet, isLoading } = useQuery({
    queryKey: ['snippet', id],
    queryFn: () => snippetApi.get(Number(id)),
    enabled: !isNew,
  });

  // Load data into form when fetched
  useEffect(() => {
    if (snippet && !isNew) {
      setFormData({
        name: snippet.name,
        description: snippet.description,
        content: snippet.content,
        category: snippet.category,
        tags: snippet.tags,
        is_active: snippet.is_active,
      });
      setVariables(snippet.variables);
    }
  }, [snippet, isNew]);

  // Detect variables in content
  useEffect(() => {
    const djangoVars = formData.content.match(/\{\{\s*(\w+)\s*\}\}/g) || [];
    const pythonVars = formData.content.match(/(?<!\{)\{(\w+)\}(?!\})/g) || [];
    const allVars = [
      ...djangoVars.map(v => v.replace(/[{}\s]/g, '')),
      ...pythonVars.map(v => v.replace(/[{}]/g, ''))
    ];
    setVariables([...new Set(allVars)]);
  }, [formData.content]);

  // Create mutation
  const createMutation = useMutation({
    mutationFn: snippetApi.create,
    onSuccess: () => {
      toast.success('Snippet created successfully');
      queryClient.invalidateQueries({ queryKey: ['snippets'] });
      navigate('/ai/snippets');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.name?.[0] || 'Failed to create snippet');
    },
  });

  // Update mutation
  const updateMutation = useMutation({
    mutationFn: (data: Partial<SnippetCreate>) =>
      snippetApi.update(Number(id), data),
    onSuccess: () => {
      toast.success('Snippet updated successfully');
      queryClient.invalidateQueries({ queryKey: ['snippets'] });
      queryClient.invalidateQueries({ queryKey: ['snippet', id] });
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.name?.[0] || 'Failed to update snippet');
    },
  });

  // Handlers
  const handleChange = (field: keyof SnippetCreate) => (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.value,
    });
  };

  const handleSwitchChange = (field: keyof SnippetCreate) => (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setFormData({
      ...formData,
      [field]: event.target.checked,
    });
  };

  const handleAddTag = () => {
    if (tagInput.trim() && !formData.tags?.includes(tagInput.trim())) {
      setFormData({
        ...formData,
        tags: [...(formData.tags || []), tagInput.trim()],
      });
      setTagInput('');
    }
  };

  const handleDeleteTag = (tagToDelete: string) => {
    setFormData({
      ...formData,
      tags: formData.tags?.filter((tag) => tag !== tagToDelete) || [],
    });
  };

  const handleSubmit = () => {
    if (isNew) {
      createMutation.mutate(formData);
    } else {
      updateMutation.mutate(formData);
    }
  };

  const handleCopyReference = () => {
    if (snippet) {
      navigator.clipboard.writeText(`[[snippet:${snippet.name}]]`);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
      toast.success('Reference syntax copied to clipboard');
    }
  };

  if (isLoading && !isNew) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <Typography>Loading...</Typography>
      </Box>
    );
  }

  return (
    <Box>
      {/* Header */}
      <Box display="flex" alignItems="center" gap={isMobile ? 1 : 2} mb={3}>
        <PageBackButton
          label="Back to Prompt Snippets"
          to="/ai/snippets"
          preferHistory={false}
          hideLabelOnMobile
        />
        <Typography variant={isMobile ? 'h5' : 'h4'}>
          {isNew ? 'New Snippet' : 'Edit Snippet'}
        </Typography>
      </Box>

      <Grid container spacing={isMobile ? 2 : 3}>
        {/* Main Form */}
        <Grid size={{ xs: 12, md: 8 }}>
          <Paper sx={{ p: isMobile ? 2 : 3 }}>
            <Box component="form" display="flex" flexDirection="column" gap={isMobile ? 2 : 3}>
              {/* Name */}
              <TextField
                label="Name"
                required
                fullWidth
                value={formData.name}
                onChange={handleChange('name')}
                helperText="Unique identifier for the snippet (alphanumeric, underscores, hyphens)"
                InputProps={{ style: { fontSize: 16 } }}
              />

              {/* Description */}
              <TextField
                label="Description"
                fullWidth
                multiline
                rows={2}
                value={formData.description}
                onChange={handleChange('description')}
                helperText="Optional description of what this snippet does"
              />

              {/* Content */}
              <TextField
                label="Content"
                required
                fullWidth
                multiline
                rows={10}
                value={formData.content}
                onChange={handleChange('content')}
                helperText="The actual prompt content. Use {{variable}} or {variable} for template variables"
                sx={{ fontFamily: 'monospace' }}
              />

              {/* Category */}
              <TextField
                label="Category"
                fullWidth
                value={formData.category}
                onChange={handleChange('category')}
                helperText="Optional category for organization (e.g., 'trading', 'analysis')"
              />

              {/* Tags */}
              <Box>
                <TextField
                  label="Tags"
                  fullWidth
                  value={tagInput}
                  onChange={(e) => setTagInput(e.target.value)}
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      handleAddTag();
                    }
                  }}
                  helperText="Press Enter to add tags"
                  InputProps={{
                    endAdornment: (
                      <Button onClick={handleAddTag} size="small">
                        Add
                      </Button>
                    ),
                  }}
                />
                <Box display="flex" gap={1} flexWrap="wrap" mt={1}>
                  {formData.tags?.map((tag) => (
                    <Chip
                      key={tag}
                      label={tag}
                      onDelete={() => handleDeleteTag(tag)}
                      size="small"
                    />
                  ))}
                </Box>
              </Box>

              {/* Active Toggle */}
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.is_active}
                    onChange={handleSwitchChange('is_active')}
                  />
                }
                label="Active"
              />

              {/* Actions */}
              <Box 
                display="flex" 
                flexDirection={isMobile ? 'column' : 'row'}
                gap={2} 
                justifyContent="flex-end"
              >
                <Button
                  variant="outlined"
                  onClick={() => navigate('/ai/snippets')}
                  fullWidth={isMobile}
                >
                  Cancel
                </Button>
                <Button
                  variant="contained"
                  startIcon={<SaveIcon />}
                  onClick={handleSubmit}
                  fullWidth={isMobile}
                  disabled={
                    !formData.name ||
                    !formData.content ||
                    createMutation.isPending ||
                    updateMutation.isPending
                  }
                >
                  {isNew ? 'Create' : 'Save'}
                </Button>
              </Box>
            </Box>
          </Paper>
        </Grid>

        {/* Sidebar */}
        <Grid size={{ xs: 12, md: 4 }}>
          {/* Reference Syntax */}
          {!isNew && snippet && (
            <Card sx={{ mb: 2 }}>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Reference Syntax
                </Typography>
                <Box
                  sx={{
                    bgcolor: isDark ? 'grey.800' : 'grey.100',
                    p: 2,
                    borderRadius: 1,
                    fontFamily: 'monospace',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                  }}
                >
                  <Typography variant="body2">
                    [[snippet:{snippet.name}]]
                  </Typography>
                  <IconButton size="small" onClick={handleCopyReference}>
                    {copied ? <CheckIcon color="success" /> : <CopyIcon />}
                  </IconButton>
                </Box>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1 }}>
                  Use this syntax to reference the snippet in expert prompts
                </Typography>
              </CardContent>
            </Card>
          )}

          {/* Variables Detected */}
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Variables Detected
              </Typography>
              {variables.length > 0 ? (
                <Box display="flex" gap={1} flexWrap="wrap">
                  {variables.map((variable) => (
                    <Chip
                      key={variable}
                      label={variable}
                      size="small"
                      color="primary"
                      variant="outlined"
                    />
                  ))}
                </Box>
              ) : (
                <Typography variant="body2" color="text.secondary">
                  No template variables found
                </Typography>
              )}
            </CardContent>
          </Card>

          {/* Usage Info */}
          {!isNew && snippet && (
            <Card>
              <CardContent>
                <Typography variant="h6" gutterBottom>
                  Usage Statistics
                </Typography>
                <Box display="flex" flexDirection="column" gap={1}>
                  <Box display="flex" justifyContent="space-between">
                    <Typography variant="body2" color="text.secondary">
                      Usage Count:
                    </Typography>
                    <Typography variant="body2" fontWeight="medium">
                      {snippet.usage_count}
                    </Typography>
                  </Box>
                  {snippet.last_used_at && (
                    <Box display="flex" justifyContent="space-between">
                      <Typography variant="body2" color="text.secondary">
                        Last Used:
                      </Typography>
                      <Typography variant="body2" fontWeight="medium">
                        {new Date(snippet.last_used_at).toLocaleString()}
                      </Typography>
                    </Box>
                  )}
                </Box>
              </CardContent>
            </Card>
          )}

          {/* Help */}
          <Alert severity="info" sx={{ mt: 2 }}>
            <Typography variant="body2" fontWeight="medium" gutterBottom>
              ðŸ’¡ Tip
            </Typography>
            <Typography variant="body2">
              Snippets can contain template variables like {'{'}{'{'} variable {'}'}{'}'} or {'{'} variable {'}'}.
              These will be replaced when the snippet is used in an expert.
            </Typography>
          </Alert>
        </Grid>
      </Grid>
    </Box>
  );
};

export default SnippetDetail;
